import { type ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';

import { useLeadModal } from '../lead-form/useLeadModal';

export interface B2BIndexHeroCard {
    title: string;
    body: string;
    ctaLabel: string;
    ctaHref?: string;
    /** When true, the CTA opens the lead modal instead of navigating. */
    ctaOpensModal?: boolean;
}

export interface B2BIndexHeroProps {
    headline: string;
    body: string;
    backgroundSrc: string;
    /** Optional stylised "A" mark — placed top-left below the nav. */
    logomarkSrc?: string;
    /** Transparent top nav rendered above the photo. */
    topNav?: ReactNode;
    /** Two glass cards anchored bottom-right. */
    cards: [B2BIndexHeroCard, B2BIndexHeroCard];
    className?: string;
}

/**
 * Hero for the B2B index page (Figma `751:25157`, 1920×1080).
 *
 * Three things differentiate this from the existing `B2BHero` (used by the
 * `/brands` and `/professionals` B2B landing pages):
 *  1. The headline sits BOTTOM-LEFT (not centred) with a paragraph below it.
 *  2. There's no centred ARCHIPRO wordmark in the nav — pass
 *     `hideWordmark` to the `B2BTopNav` instance you provide via `topNav`.
 *  3. Two frosted "Learn More" cards float bottom-right linking to the
 *     audience-specific landings (For Architects & Designers → /suppliers,
 *     For Brands → /brands).
 *
 * 1920-frame absolute layout (all sizes vw against 1920):
 *  - Background photo: full bleed, rgba(0,0,0,0.2) overlay, top gradient
 *    band (h=378, from black/40 to transparent) for nav legibility.
 *  - Logomark: (50, 210), 219.576 × 166 → 2.604 % / 19.444 % / 11.436 vw / 8.646 vw.
 *  - Headline: (69.8, 669.06), 507.992 wide, 33.48 px = 1.744 vw, leading 1.23.
 *  - Body: (69.8, 788.1), 555.203 wide, 23.476 px = 1.223 vw, leading 1.23.
 *  - Card 1 (For Architects & Designers): (960, 550.1), 449.948 × 435.837.
 *  - Card 2 (For Brands): (1436.55, 550.1), 450.73 × 435.837.
 *  - Both cards: bg rgba(249,246,246,0.2), rounded 7.825 px,
 *    title 53.211 px = 2.772 vw, body 23.476 px = 1.223 vw,
 *    pill 51.723 tall, label 17.499 px = 0.911 vw.
 */
export const B2BIndexHero = ({
    headline,
    body,
    backgroundSrc,
    logomarkSrc,
    topNav,
    cards,
    className,
}: B2BIndexHeroProps) => {
    return (
        <section
            className={cn(
                'relative w-full overflow-hidden text-[#f9f9f7]',
                // Full-bleed: fill the viewport. `svh` avoids the iOS Safari
                // toolbar jump that `vh` causes. Absolute-positioned children
                // use percentage offsets against this section, so they stay
                // anchored to the corners as the viewport ratio changes.
                'h-[100svh] w-full',
                className,
            )}
        >
            <img
                className="absolute inset-0 size-full object-cover"
                src={backgroundSrc}
                alt=""
            />
            {/* Flat 20% black wash. */}
            <div className="absolute inset-0 bg-black/20" />
            {/* Top gradient band for nav legibility (378/1080 = 35%). */}
            <div className="absolute inset-x-0 top-0 h-[35%] bg-gradient-to-b from-black/40 to-transparent" />

            {topNav}

            {/* Stylised "A" logomark.
             *  - Desktop (1920 frame): (50, 210), 219.576 × 166.
             *  - Mobile: large brand anchor below the nav — ~40vw wide so it
             *    reads as the hero's primary mark (per design direction). */}
            {logomarkSrc && (
                <img
                    src={logomarkSrc}
                    alt=""
                    aria-hidden
                    className="absolute max-md:left-[5.4%] max-md:top-[14vw] max-md:h-[30vw] max-md:w-[40vw] md:left-[2.604%] md:top-[19.444%] md:h-[8.646vw] md:w-[11.436vw]"
                />
            )}

            {/* Desktop layout — absolute-positioned to the 1920×1080 frame. */}
            <div className="max-md:hidden">
                <h1 className="absolute left-[3.635%] top-[61.95%] w-[26.46vw] font-sans text-[1.744vw] font-medium leading-[1.23] tracking-[-0.020em]">
                    {headline}
                </h1>
                <p className="absolute left-[3.635%] top-[73.0%] w-[28.92vw] font-sans text-[1.223vw] font-medium leading-[1.23] tracking-[-0.020em]">
                    {body}
                </p>
                <CardSlot card={cards[0]} leftPercent={50} />
                <CardSlot card={cards[1]} leftPercent={74.821} />
            </div>

            {/* Mobile layout — bottom panel with gradient. Headline + body +
             *  two stacked cards take the lower half of the viewport; photo
             *  fills the upper half. Phase-2 mobile tokens against 414. */}
            <div className="absolute inset-x-0 bottom-0 hidden flex-col gap-[4vw] bg-gradient-to-t from-black/80 via-black/50 to-transparent px-[5.4%] pb-[6vw] pt-[18vw] max-md:flex">
                <h1 className="font-sans text-[6.5vw] font-medium leading-[1.1] tracking-[-0.020em]">
                    {headline}
                </h1>
                <p className="font-sans text-[3.5vw] font-medium leading-[1.35] tracking-[-0.020em] text-white/85">
                    {body}
                </p>
                <div className="mt-[2vw] flex flex-col gap-[3vw]">
                    <MobileCard card={cards[0]} />
                    <MobileCard card={cards[1]} />
                </div>
            </div>
        </section>
    );
};

interface CardSlotProps {
    card: B2BIndexHeroCard;
    leftPercent: number;
}

const CardSlot = ({ card, leftPercent }: CardSlotProps) => {
    const { openModal } = useLeadModal();

    const pillClassName =
        'inline-flex items-center justify-center gap-[0.641vw] rounded-full bg-[#1e1e1e] pl-[1.924vw] pr-[1.385vw] py-[0.625vw] h-[2.694vw] text-[0.911vw] font-medium leading-[1.475vw] text-white shadow-[0_0.104vw_0.729vw_rgba(0,0,0,0.11)] transition-opacity hover:opacity-90';

    const pillBody = (
        <>
            {card.ctaLabel}
            {/* `fill-current` makes the arrow inherit the pill's text color
             *  (white). The source SVG has no explicit fill, so without this
             *  it defaults to black and disappears against the dark pill. */}
            <ArrowRight
                className="h-[0.696vw] w-[1.254vw] fill-current"
                aria-hidden
            />
        </>
    );

    return (
        <div
            className="absolute aspect-[449.948/435.837] w-[23.435vw] overflow-hidden rounded-[0.407vw] bg-[rgba(249,246,246,0.2)] backdrop-blur-md"
            style={{
                left: `${leftPercent}%`,
                top: `${(550.1 / 1080) * 100}%`,
            }}
        >
            <h2 className="absolute left-[5.216%] top-[5.268%] w-[85.32%] font-sans text-[2.772vw] font-medium leading-[0.95] tracking-[-0.030em]">
                {card.title}
            </h2>
            <p className="absolute left-[6.957%] top-[54.61%] w-[78.23%] font-sans text-[1.223vw] font-medium leading-[1.23] tracking-[-0.020em]">
                {card.body}
            </p>
            <div className="absolute left-[6.957%] top-[75.7%]">
                {card.ctaOpensModal ? (
                    <button
                        type="button"
                        onClick={() => openModal({ ctaLabel: card.ctaLabel })}
                        className={pillClassName}
                    >
                        {pillBody}
                    </button>
                ) : (
                    <RoccoLink
                        to={card.ctaHref ?? '#'}
                        className={pillClassName}
                    >
                        {pillBody}
                    </RoccoLink>
                )}
            </div>
        </div>
    );
};

/**
 * Mobile card — stacked flowed layout for the bottom panel of the index hero.
 * Uses the Phase-2 vw tokens (rem-equivalent sizes scaled to a 414 baseline).
 */
const MobileCard = ({ card }: { card: B2BIndexHeroCard }) => {
    const { openModal } = useLeadModal();

    const pillClassName =
        'inline-flex h-[8.75vw] items-center gap-[1.5vw] rounded-full bg-[#1e1e1e] pl-[5vw] pr-[3.5vw] text-[3.31vw] font-medium leading-[1.07] text-white shadow-[0_0.5vw_2vw_rgba(0,0,0,0.18)] transition-opacity hover:opacity-90';

    const pillBody = (
        <>
            {card.ctaLabel}
            <ArrowRight
                className="h-[2.4vw] w-[4.2vw] fill-current"
                aria-hidden
            />
        </>
    );

    return (
        <div className="flex flex-col gap-[3vw] rounded-[2vw] bg-[rgba(249,246,246,0.18)] p-[5vw] backdrop-blur-md">
            <h2 className="font-sans text-[5.5vw] font-medium leading-[1.05] tracking-[-0.030em]">
                {card.title}
            </h2>
            <p className="font-sans text-[3.255vw] font-medium leading-[1.3] tracking-[-0.020em] text-white/90">
                {card.body}
            </p>
            <div className="mt-[1vw]">
                {card.ctaOpensModal ? (
                    <button
                        type="button"
                        onClick={() => openModal({ ctaLabel: card.ctaLabel })}
                        className={pillClassName}
                    >
                        {pillBody}
                    </button>
                ) : (
                    <RoccoLink
                        to={card.ctaHref ?? '#'}
                        className={pillClassName}
                    >
                        {pillBody}
                    </RoccoLink>
                )}
            </div>
        </div>
    );
};
